import * as TemplateTypes from 'types/template.types';
import _, { startCase, toLower } from 'lodash';

const reverseDirection = (direction: TemplateTypes.Direction) : TemplateTypes.Direction => {
    switch (direction){
        case 'bottom': return 'top';
        case 'top': return 'bottom';
        case 'left': return 'right';
        case 'right': return 'left';
    }
}

const weightedRandomObj = <T> (options: { item: T, weight: number }[]) => {
    let i;
    let weights: number[] = [];

    for (i = 0; i < options.length; i++)
        weights[i] = options[i].weight + (weights[i - 1] || 0);
    
    var random = Math.random() * weights[weights.length - 1];
    
    for (i = 0; i < weights.length; i++)
        if (weights[i] > random)
            break;
    
    return options[i].item;
}


const weightedRandomArr = <T> (items: T[], weights: number[]) => {
    let i;

    for (i = 0; i < weights.length; i++)
        weights[i] += weights[i - 1] || 0;
    
    let random = Math.random() * weights[weights.length - 1];
    
    for (i = 0; i < weights.length; i++)
        if (weights[i] > random)
            break;

    return items[i];
}

/**
 * Pass the number of weights you need and the three points on a Quadratic Curve and
 * this method will return an array where each element represents its index's position
 * on the curve.
 * 
 * Useful for generating difficulty ramp weighting.
 * 
 * @param count - number of weights to generate on curve
 * @param p0 - start point
 * @param p1 - control point 1
 * @param p2 - end poiint
 * @returns 
 */
const generateWeightsQuadratic = (count: number, p0: number, p1: number, p2: number) => {
    let weights = [], increment = 1 / count;
    for (let i = 0; i < count; i++){
        weights.push(Phaser.Math.Interpolation.QuadraticBezier((i + 1) * increment, p0, p1, p2));
    }
    return weights;
}


/**
 * Pass the number of weights you need and the four points on a Cubic Curve and
 * this method will return an array where each element represents its index's position
 * on the curve.
 * 
 * @param count - number of weights to generate on curve
 * @param p0 - start point
 * @param p1 - control point 1
 * @param p2 - control point 2
 * @param p3 - end poiint
 */
 const generateWeightsCubic = (count: number, p0: number, p1: number, p2: number, p3: number) => {
    let weights = [], increment = 1 / count;
    for (let i = 0; i < count; i++){
        weights.push(Phaser.Math.Interpolation.CubicBezier((i + 1) * increment, p0, p1, p2, p3));
    }
    return weights;
}


const weightedRandomQuadratic = <T> (items: T[], p0: number, p1: number, p2: number) => {
    let w = weightedRandomArr<T>(items, generateWeightsQuadratic(items.length, p0, p1, p2));
    return w;
}

const weightedRandomCubic = <T> (items: T[], p0: number, p1: number, p2: number, p3: number) => {
    return weightedRandomArr<T>(items, generateWeightsCubic(items.length, p0, p1, p2, p3));
}

const mode = <T> (arr: T[]) => {
    return arr.sort((a,b) =>
          arr.filter(v => v === a).length
        - arr.filter(v => v === b).length
    ).pop();
}

/**
 * (ChatGPT)
 * 
 * Merges two objects but ignores properties which are not present on the initial/first object.
 * 
 * @param obj1 
 * @param obj2 
 * @returns 
 */
function mergeObjectsAndIgnoreMissingProperties<T>(obj1: T, obj2: T): T {
    const mergedObject: T = _.merge({}, obj1, obj2, (objValue: any, srcValue: any) => {
      if (_.isUndefined(objValue)) {
        // Ignore properties not present in obj1
        return objValue;
      }
      // Use the value from obj2 for merging
      return srcValue;
    });
  
    return mergedObject;
}

type NoArray<T> = T extends any[] ? never : T;

/**
 * Returns an item from localStorage or the default item if no such item exists. Performs a deep merge from default
 * object to whatever is stored in localStorage. The returned object will always match the schema of the given default
 * object. Excess properties which may be leftover in localStorage will be removed.
 * 
 * @param key - a localStorage key
 * @param defaultObj - default object if the given key doesn't exist in localStorage or is not a valid JSON
 * @returns 
 */
const fromLocalStorage = <T extends { [key:string]:any }> (key: string, defaultObj: NoArray<T>) : T => {
    let json = localStorage.getItem(key);
    if (json){
        try {
            let obj = JSON.parse(json);
            if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return defaultObj;
            return <T> mergeObjectsAndIgnoreMissingProperties(defaultObj, obj);
        } catch (err){
            return defaultObj;
        }
    }
    return defaultObj;
}

const toLocalStorage = <T> (key: string, objToMerge: Partial<T>) :  Partial<T> => {
    let json = localStorage.getItem(key);
    let item;
    try {
        item = _.merge({}, JSON.parse(json ?? '{}'), objToMerge);
    } catch (err){
        item = objToMerge;
    }

    localStorage.setItem(key, JSON.stringify(item));
    return item;
}

/**
 * Given a localStorage key and an object, merges the given object into the existing on in localStorage if
 * it exists. Does not work for localStorage items which are just arrays.
 * 
 * @param key - the local storage key of an item stored as an object
 * @param objToMerge - a partial or complete version of the stored object to perform a shallow merge on
 * @returns 
 */
const mergeIntoLocalStorage = <T> (key: string, objToMerge: Partial<T>) :  Partial<T> => {
    let json = localStorage.getItem(key);
    let item;
    try {
        item = { 
            ...JSON.parse(json ?? '{}'), 
            ...objToMerge 
        };
    } catch (err){
        item = objToMerge;
    }

    localStorage.setItem(key, JSON.stringify(item));
    return item;
}

/**
 * Converts milliseconds to formatted `hh:mm:ss`. ChatGPT wrote this one.
 * 
 * 
 * @param milliseconds - number
 * @returns 
 */
const formatMilliseconds = (milliseconds: number) : string => {
    // Convert milliseconds to seconds
    const seconds = Math.floor(milliseconds / 1000);
  
    // Calculate hours, minutes, and remaining seconds
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
  
    // Format the time as a string
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  
    return timeString;
}






/**
 * Returns a promise which will resolve after the specified amount of time to the value provided (optional).
 * 
 * @param duration 
 * @param returnVal 
 * @returns 
 */
function delay(durationMs: number, returnVal?: undefined) : Promise<void>;
function delay<ReturnType>(durationMs: number, returnVal: ReturnType) : Promise<ReturnType>;
function delay<ReturnType>(durationMs: number, returnVal: ReturnType | undefined) : Promise<ReturnType | undefined> {
    return new Promise(res => {
        window.setTimeout(() => res(returnVal), durationMs)
    })
}

const clone = (obj: { [key:string]: any }) => JSON.parse(JSON.stringify(obj))

/**
 * Returns an element from the given array so long as it is not included in the exclusion array or equals the exclusion value.
 * Note that if the exclusion array includes the entire selection array, this method will cause an infinite loop.
 * 
 * @param arr 
 * @param exclude 
 * @returns 
 */
const randomUnique = <T> (arr: T[], exclude: T | T[]) : T => {
    let el = Phaser.Utils.Array.GetRandom(arr);
    if ((Array.isArray(exclude) && exclude.includes(el)) || el === exclude) {
        return randomUnique(arr, exclude);
    } 
    return el;
}

const resolvePercentX = (scene: Phaser.Scene, x: number | string) => {
    if (typeof x === 'string'){
        return (parseInt(x) / 100) * scene.cameras.main.width;
    }
    return x * scene.cameras.main.width;
}

const resolvePercentY = (scene: Phaser.Scene, y: number | string) => {
    if (typeof y === 'string'){
        return (parseInt(y) / 100) * scene.cameras.main.height;
    }
    return y * scene.cameras.main.height;
}

const resolvePercentCoords = (scene: Phaser.Scene, x: string | number, y: string | number) => {
    return {
        x: resolvePercentX(scene, x),
        y: resolvePercentY(scene, y)
    }
}

/**
 * Given an integer and a min/max range, returns the number if it is within range and wraps it around if it is not.
 */
const wrapAround = (value: number, min: number, max: number) => {
    const range = max - min + 1;
    const offsetValue = value - min;
    const wrappedValue = ((offsetValue % range) + range) % range;
    return wrappedValue + min;
}

/**
 * This function takes in an array and chunks it into smaller arrays of the given explicit sizes.
 * (Written by copilot.)
 * 
 * @param arr 
 * @param chunkSizes 
 * @returns 
 */
function chunkArray<T>(arr: T[], chunkSizes: number[]): T[][] {
    const result: T[][] = [];
    let index = 0;
    for (const size of chunkSizes) {
        result.push(arr.slice(index, index + size));
        index += size;
    }
    return result;
}

function swap<T>(arr: T[], index1: number, index2: number): void {
    const temp = arr[index1];
    arr[index1] = arr[index2];
    arr[index2] = temp;
}
  
const titleCase = (str: string) => startCase(toLower(str));

export {
    reverseDirection,
    weightedRandomObj,
    weightedRandomArr,
    generateWeightsQuadratic,
    generateWeightsCubic,
    weightedRandomQuadratic,
    weightedRandomCubic,
    mode,
    fromLocalStorage,
    toLocalStorage,
    mergeIntoLocalStorage,
    formatMilliseconds,
    delay,
    clone,
    randomUnique,
    resolvePercentX,
    resolvePercentY,
    resolvePercentCoords,
    wrapAround,
    chunkArray,
    swap,
    titleCase
}